import { z } from 'zod'
import { UUIDTypes } from 'uuid'
import { AccountInfo } from '@azure/msal-browser'
import { useMsal } from '@azure/msal-react'
import {
  Text,
  TextInput,
  Button,
  Stack,
  Group,
  Autocomplete,
  AutocompleteProps,
  Textarea,
  Flex,
  Modal,
  Switch,
} from '@mantine/core'
import { useForm, zodResolver } from '@mantine/form'
import { IconMailForward } from '@tabler/icons-react'
import { useMemo } from 'react'
import {
  useAuthenticatedGraphMutation,
  useAuthenticatedGraphQuery,
} from '@/hooks/use-authenticated-query'
import { SendWoolstoreSpeciEmailGql } from '@/graphql/mutations/speci/send-woolstore-speci-email.graphql'
import { WoolStoreDirectoryGql } from '@/graphql/queries/speci/wool-store-directory.graphql'
import { MutationResponse } from '@loom-api-types'
import {
  showErrorNotification,
  showSuccessNotification,
} from '@shared/helpers/notifications/notifications'

export interface EmailSpeciModalProps {
  speciId: UUIDTypes
  isModalOpen: boolean
  closeModal: () => void
  brand: string
  successNavigate?: () => void
}

export const EmailSpeciModal = ({
  speciId,
  isModalOpen = false,
  closeModal,
  brand,
  successNavigate = () => {},
}: EmailSpeciModalProps) => {
  // Get user account info
  const { instance } = useMsal()
  const account: AccountInfo | null = useMemo(() => instance.getActiveAccount(), [instance])

  // The wool store allow-list is backend-config-driven (see WoolStoreDirectoryConfig), so the Hub's
  // dropdown always reflects the same list the server enforces, rather than a hardcoded, drift-prone copy.
  // This modal is permanently mounted by its parent pages (only `isModalOpen` toggles visibility), so
  // fetching unconditionally here means the directory is already loaded by the time the user opens the
  // modal, rather than showing a "Loading wool stores…" placeholder at that point.
  const {
    data: woolStoreDirectoryData,
    isLoading: isWoolStoreDirectoryLoading,
    isError: isWoolStoreDirectoryError,
  } = useAuthenticatedGraphQuery({
    queryKey: ['woolStoreDirectory'],
    gql: WoolStoreDirectoryGql,
  })

  const woolStoreDirectory = useMemo(
    () => woolStoreDirectoryData?.woolStoreDirectory ?? [],
    [woolStoreDirectoryData]
  )
  const woolStoreEmails = useMemo(
    () => woolStoreDirectory.map((entry) => entry.email),
    [woolStoreDirectory]
  )
  const woolStoreEmailLabels = useMemo(
    () => Object.fromEntries(woolStoreDirectory.map((entry) => [entry.email, entry.label])),
    [woolStoreDirectory]
  )

  const renderAutocompleteOption: AutocompleteProps['renderOption'] = ({ option }) => (
    <Group gap='sm'>
      <Text size='sm'>{option.value}</Text>
      <Text size='sm' c='dimmed'>
        {woolStoreEmailLabels[option.value]}
      </Text>
    </Group>
  )

  // Disable submission while the allow-list is loading (so a user can't submit before validation
  // is even possible) and on error (rather than silently falling back to unrestricted free text,
  // which would defeat the point of the backend allow-list).
  const isWoolStoreDirectoryUnavailable = isWoolStoreDirectoryLoading || isWoolStoreDirectoryError

  // Email mutation endpoint
  const sendEmailMutation = useAuthenticatedGraphMutation({
    gql: SendWoolstoreSpeciEmailGql,
    gqlKey: 'sendToWoolStore',
    onSuccess: (mutation) => {
      const responseData: MutationResponse = mutation.sendToWoolStore

      if (responseData.success) {
        showSuccessNotification('Email sent to wool store successfully')
        closeModal()
        successNavigate()
      } else {
        showErrorNotification(responseData.errorMessage || 'An error occurred')
      }
    },
  })

  // Form validation schema
  const formSchema = z.object({
    woolStoreEmailAddress: z
      .string()
      .min(1, 'Email is required')
      .pipe(z.string().email('Invalid email address')),
    emailSubject: z.string().min(1, 'Subject is required'),
    emailMessage: z.string().min(1, 'Message is required'),
    bccMyself: z.boolean().optional(),
  })

  // Form
  const form = useForm({
    mode: 'uncontrolled',
    initialValues: {
      woolStoreEmailAddress: '',
      emailSubject: `Wool specification for ${brand}`,
      emailMessage: `A Wool Specification has been completed for ${brand}${account ? ` by ${account?.name}.` : `.`}\n\nPlease click on this link to take you to the Zentera Wool Specification.`,
      bccMyself: false,
    },
    validate: zodResolver(formSchema),
  })

  const woolStoreEmailAddressFieldProps = form.getInputProps('woolStoreEmailAddress')

  return (
    <Modal
      opened={isModalOpen}
      onClose={closeModal}
      size='lg'
      centered
      title={
        <Flex justify='flex-start' align='center'>
          <IconMailForward size={28} color='var(--mantine-color-gray-4)' />
          <Text fw={500} size='lg' ml='sm'>
            Send email to woolstore
          </Text>
        </Flex>
      }
      closeButtonProps={{ 'aria-label': 'Close modal' }}
      returnFocus={false}
    >
      <form onSubmit={form.onSubmit((values) => sendEmailMutation.mutate({ ...values, speciId }))}>
        <Stack>
          <Autocomplete
            label='Send email to'
            placeholder={
              isWoolStoreDirectoryLoading
                ? 'Loading wool stores…'
                : isWoolStoreDirectoryError
                  ? 'Unable to load wool store list'
                  : 'Select woolstore'
            }
            disabled={isWoolStoreDirectoryUnavailable}
            data={woolStoreEmails}
            renderOption={renderAutocompleteOption}
            key={form.key('woolStoreEmailAddress')}
            {...woolStoreEmailAddressFieldProps}
            error={
              isWoolStoreDirectoryError
                ? 'Unable to load the wool store list. Please try again later.'
                : woolStoreEmailAddressFieldProps.error
            }
          />
          <Switch
            label='BCC Myself'
            key={form.key('bccMyself')}
            {...form.getInputProps('bccMyself', { type: 'checkbox' })}
          />
          <TextInput
            label='Subject'
            key={form.key('emailSubject')}
            {...form.getInputProps('emailSubject')}
          />
          <Textarea
            label='Message'
            description='A link to the speci will appear below your message in the email.'
            inputWrapperOrder={['label', 'input', 'error', 'description']}
            autosize
            minRows={3}
            key={form.key('emailMessage')}
            {...form.getInputProps('emailMessage')}
          />
          <Group justify='flex-end' mt='md' gap='xs'>
            <Button
              variant='transparent'
              onClick={closeModal}
              aria-label='Cancel sending email to woolstore'
            >
              Cancel
            </Button>
            <Button
              type='submit'
              loading={sendEmailMutation.isPending}
              disabled={sendEmailMutation.isPending || isWoolStoreDirectoryUnavailable}
            >
              Send email
            </Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  )
}
